import { SupplierChip } from "@/shared/components/SupplierChip";
import { ColDef } from "ag-grid-enterprise";
import moment from "moment";
import { ChemistryRow } from "../models/ChemistryRow";
import {
	EXCEL_BELOW_DETECTION_CLASS,
	EXCEL_CENTER_CLASS,
	EXCEL_TEXT_CLASS,
	numberExcelClass,
	supplierExcelClass,
} from "../utils/excelStyles";

export const ELEMENT_CONFIG = {
	C: { decimals: 3 },
	Mn: { decimals: 2 },
	Si: { decimals: 2 },
	Cu: { decimals: 2 },
	Ni: { decimals: 2 },
	Cr: { decimals: 2 },
	P: { decimals: 3 },
	S: { decimals: 3 },
	Mo: { decimals: 3 },
	Sn: { decimals: 3 },
	Al: { decimals: 3 },
	V: { decimals: 4 },
	Nb: { decimals: 4 },
	N: { decimals: 4 },
	B: { decimals: 4 },
	Ti: { decimals: 4 },
	Ca: { decimals: 4 },
	Pb: { decimals: 3 },
	As: { decimals: 3 },
	Sb: { decimals: 3 },
	H: { decimals: 3 },
	W: { decimals: 3 },
	Co: { decimals: 3 },
} as const;

export type ElementSymbol = keyof typeof ELEMENT_CONFIG;

export const ELEMENTS = Object.keys(ELEMENT_CONFIG) as ElementSymbol[];

export const isBelowDetection = (
	belowDetection: string | null | undefined,
	symbol: ElementSymbol,
) =>
	(belowDetection ?? "")
		.split(",")
		.map((s) => s.trim())
		.includes(symbol);

export const formatElementValue = (
	value: unknown,
	symbol: ElementSymbol,
	belowDetection?: string | null,
) => {
	if (value === null || value === undefined || value === "") return "";
	const n = Number(value);
	if (Number.isNaN(n)) return "";

	const decimals = ELEMENT_CONFIG[symbol].decimals;
	const formatted = String(n.toFixed(decimals));
	return isBelowDetection(belowDetection, symbol)
		? `< ${formatted}`
		: formatted;
};

const formatElement = (
	params: { value: unknown; data?: { BelowDetection?: string | null } },
	symbol: ElementSymbol,
) => formatElementValue(params.value, symbol, params.data?.BelowDetection);

export const useTableData = (rows: ChemistryRow[] = []) => {
	const activeElements =
		rows.length === 0
			? ELEMENTS
			: ELEMENTS.filter((el) =>
					rows.some((r) => r[el as keyof ChemistryRow] != null),
				);

	const columns: ColDef[] = [
		{
			field: "Supplier",
			headerName: "Supplier",
			cellRenderer: SupplierChip,
			cellClass: (params) => supplierExcelClass(params.value),
			flex: 0.3,
			sortable: true,
		},
		{
			field: "HeatName",
			headerName: "Heat",
			cellClass: EXCEL_TEXT_CLASS,
			flex: 0.4,
			sortable: true,
		},
		{
			field: "Grade",
			headerName: "Grade",
			valueGetter: (params) => params.data?.Grade || "",
			cellClass: EXCEL_TEXT_CLASS,
			flex: 0.4,
			sortable: true,
		},
		{
			field: "Reported",
			headerName: "Reported At",
			valueGetter: (params) =>
				params.data?.Reported
					? moment(params.data.Reported).format("MM/DD/YY")
					: "",
			cellClass: EXCEL_CENTER_CLASS,
			flex: 0.5,
			sortable: true,
		},
		...activeElements.map(
			(el): ColDef => ({
				field: el,
				headerName: el,
				valueFormatter: (params) => formatElement(params, el),
				cellClass: (params) =>
					isBelowDetection(params.data?.BelowDetection, el)
						? EXCEL_BELOW_DETECTION_CLASS
						: numberExcelClass(ELEMENT_CONFIG[el].decimals),
				flex: 0.3,
				sortable: true,
				cellStyle: { justifyContent: "flex-end" },
			}),
		),
	];

	return { columns };
};
